import CTNFilterBar, { CTNFilterButton, CTNFilterCalendarRange, CTNFilterSearch } from '@ctn/ui/components/ctn-filter-bar';
import CTNTabNavigation from '@ctn/ui/components/ctn-tab-navigation';
import CTNTable from '@ctn/ui/components/ctn-table';
import { type CTNButtonProps } from '@ctn/ui/types/ctn-button';
import { type CTNFilterBarRef } from '@ctn/ui/types/ctn-filter-bar';
import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useForm } from 'react-hook-form';

import PageHeader from '@/components/PageHeader';
import { useGlobalContext } from '@/context/global.context';
import { usePageContext } from '@/context/page.context';
import { getListadoSocios, geListadoSociosXLS } from '@/services/socios.service';

import { ROUTES_LABEL } from '@/constants/routes.constants';
import { PAGE_SIZE } from '@/constants/table.constants';
import { downloadFile } from '@/utils/file.utils';

import { getColumns } from './columns';
import { defaultValues, getSchema, searchFields, type FilterFormData } from './config';
import { type Socio } from './interfaces/socio.interface';

const TAB_ID = {
	actual: 'actual',
	historico: 'historico',
};

const LibroSocios = () => {
	const filterBarRef = useRef<CTNFilterBarRef>(null);
	const { user } = useGlobalContext();
	const { initBreadcrumb } = usePageContext();
	const [selectedTab, setSelectedTab] = useState<string>(TAB_ID.actual);
	const [data, setData] = useState<Socio[]>([]);
	const [filteredData, setFilteredData] = useState<Socio[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const { watch, control, reset, setValue } = useForm<FilterFormData>({
		resolver: getSchema(),
		mode: 'onChange',
		defaultValues: defaultValues(),
	});

	const search = watch('search');
	const fecha = watch('fecha');

	const onDownloadDocumento = async () => {
		const result = await geListadoSociosXLS();
		downloadFile({ data: result.data, name: 'socios.xls', mimeType: 'application/vnd.ms-excel' });
	};

	const actions: CTNButtonProps[] = [
		{ 
			label: 'Descargar XLS', 
			mode: 'primary', 
			size: 'md',  
			onClick() {
				onDownloadDocumento();
			}
		},
	];

	useEffect(() => {
		initBreadcrumb(ROUTES_LABEL.LIBRO_SOCIOS);
	}, []);

	const getListado = useCallback(async () => {
		setIsLoading(true); 
		try {
			const isHistorico = selectedTab === TAB_ID.historico;
			const { code, data: resultData } = await getListadoSocios(isHistorico, user);
			if (code === 200) {
				setData(resultData ?? []);
				setFilteredData(resultData ?? []);
			} else {
				setData([]);
				setFilteredData([]);
			}
		} catch (e) {
			setData([]); 
			setFilteredData([]);
		} finally {
			filterBarRef.current?.resetFilter();
			setIsLoading(false); 
		}
	}, [selectedTab]);

	useEffect(() => {
		getListado();
	}, [getListado]);

	useEffect(() => {
		const filtered = data.filter((socio) => {
			if (fecha && fecha.length >= 1 && fecha[0]) {
				const partes = socio.fecha.split('/'); 
				if (partes.length !== 3) return false; 
            
				const dia = parseInt(partes[0], 10);
				const mes = parseInt(partes[1], 10) - 1; 
				const anio = parseInt(partes[2], 10);

				const fechaSocio = new Date(anio, mes, dia);
				const fechaInicio = new Date(fecha[0]);

				if (fechaSocio < fechaInicio) {
					return false;
				}

				if (fecha[1]) {
					const fechaFin = new Date(fecha[1]);
					if (fechaSocio > fechaFin) {
						return false;
					}
				}
			}

			if (search && search.length > 0) {
				return search.every((criterion) => {
					const campo = criterion.key as keyof Socio;
					const valorBusqueda = criterion.search?.toLowerCase().trim();

					if (!valorBusqueda) return true;

					const valorSocio = socio[campo];
					if (valorSocio !== undefined && valorSocio !== null) {
						return valorSocio.toString().toLowerCase().includes(valorBusqueda);
					}

					return false;
				});
			}

			return true;
		});

		setFilteredData(filtered);
	}, [search, fecha, data]);

	const columns = useMemo(() => getColumns(), []);

	const tabs = [
		{
			key: TAB_ID.actual,
			header: 'Actual',
		},
		{
			key: TAB_ID.historico,
			header: 'Histórico',
		},
	];

	const handleTabChange = (tabObj: any) => {
		const targetTab = tabObj?.key || tabObj?.index || tabObj;
		if (targetTab) {
			setSelectedTab(targetTab);
		}
	}; 

	const hideTabs = true;


	return (
		<>
			<PageHeader hasHelp title={ROUTES_LABEL.LIBRO_SOCIOS} className="u-mt-8" actions={actions} />
			<CTNTabNavigation
				className="u-mt-24"
				autoAction={false}
				activeTab={selectedTab}
				tabs={tabs}
				onTabChange={(tab) => handleTabChange(tab)}
			/>
			<CTNFilterBar
				ref={filterBarRef}
				tagBar
				show
				className="c-pnp-filter-bar u-mt-16"
				control={control as any}
				setValue={setValue as any}
				reset={reset as any}
			>
				<CTNFilterSearch
					name="search" 
					label="Buscar"
					tag="Búsqueda"
					placeholder='Búsqueda'
					options={searchFields} />
				<CTNFilterCalendarRange
					label="Fecha"
					placeholder="Fecha"
					name="fecha"
					tag="Fecha"
					maxDate={new Date()}
					tagFormat="dd/MM/yyyy"
					locale="es"
				/>
				<CTNFilterButton
					name="clearAll"
					label="Reiniciar filtros"
					tag="Limpiar"
					icon={[{ icon: 'restartCircle', position: 'left', color: 'blue-60' }]}
					onClick={() => filterBarRef.current?.resetFilter()}
				/>
			</CTNFilterBar>
			<div className='[ c-pnp-book-partners__container ] [ u-mt-16 ]'> 
				<CTNTable
					forcePagination
					idData="id"
					loading={isLoading} 
					loadingConf={{ spinner: true }}
					columns={columns}
					data={filteredData}
					totalData={filteredData.length}
					noDataConf={{ description: 'No hay ningun socio' }}
					pageSize={PAGE_SIZE.DEFAULT}
				/>
			</div>
		</>
	);
};

export default LibroSocios;